import _ from 'lodash';

import isPaceMatrix from 'types/visTypeCheckers/isPaceMatrix';
import isSingleMetricDateMatrix from 'types/visTypeCheckers/isSingleMetricDateMatrix';

export type LegacyMatrix =
  | VisualisationDefinitions.SingleMetricDateMatrix
  | VisualisationDefinitions.PaceMatrix;

type LegacyMatrixContent = Pick<
  Studio.ChartContent,
  'visType' | 'metricIds' | 'description' | 'dimensions' | 'dimensionOptions'
> & { table?: Studio.TableOptions };

// The windows each value can be compared with, as the classic comparison
// selector offers them for each (ComparisonSelector)
export const DATE_COMPARISON_BASELINES: Partial<
  Record<RelativeDateRangeType, string>
> = {
  'last 7 days': 'Previous 7 days',
  'last 14 days': 'Previous 14 days',
  'last 30 days': 'Previous 30 days',
  'last 60 days': 'Previous 60 days',
  'last 90 days': 'Previous 90 days',
  'last 180 days': 'Previous 180 days',
  'last 365 days': 'Previous 365 days',
  'last week': 'Previous week',
  'last 4 weeks': 'Previous 4 weeks',
  'last month': 'Previous month',
  'last year': 'Previous year',
};
export const PACE_BASELINES: Partial<Record<RelativeDateRangeType, string>> = {
  'last week': 'Last week',
  'last 4 weeks': 'Last 4 weeks',
  'last 13 weeks': 'Last 13 weeks',
};
const DEFAULT_DATE_COMPARISON_BASELINE: RelativeDateRangeType = 'last 7 days';
const DEFAULT_PACE_BASELINE: RelativeDateRangeType = 'last 4 weeks';

export const DEFAULT_DATE_COMPARISON_INTERVAL = 'day';
export const DEFAULT_DATE_COMPARISON_LIMIT = 20;
export const DEFAULT_PACE_SORT: VisualisationDefinitions.PaceMatrix['sortOrder'] =
  { by: 'Term', direction: 'asc' };

// Whether a Table is saved as a legacy Single Metric Date Matrix or Pace
// Matrix, rather than a V5 matrix
export const isLegacyMatrixTable = (
  content: Pick<Studio.ChartContent, 'visType'> & {
    table?: Studio.TableOptions;
  },
): boolean =>
  content.visType === 'table' &&
  !!content.table &&
  (content.table.rows === 'dateComparison' || content.table.rows === 'pace');

const getBaseline = (
  table: Studio.TableOptions | undefined,
): Studio.TableBaseline | undefined =>
  table && table.rows === 'pace'
    ? table.pace?.baseline
    : table?.dateComparison?.baseline;

// Whether the table follows the report's comparison
export const followsReportBaseline = (content: LegacyMatrixContent): boolean =>
  isLegacyMatrixTable(content) && getBaseline(content.table) === 'report';

// The comparison a date comparison or pace table saves on its gadget: its
// baseline window, or none when it follows the report's
export const getLegacyMatrixComparison = (
  table: Studio.TableOptions | undefined,
): PersistedComparisonType | undefined => {
  const baseline = getBaseline(table);
  if (baseline === 'report') {
    return undefined;
  }
  return {
    compareType: 'compareTo',
    relativeDateOption:
      baseline ??
      (table && table.rows === 'pace'
        ? DEFAULT_PACE_BASELINE
        : DEFAULT_DATE_COMPARISON_BASELINE),
  };
};

// Whether a legacy matrix can show the comparison: a window it offers
export const isLegacyMatrixComparison = ({
  chartDef,
  comparison,
}: {
  chartDef: LegacyMatrix;
  comparison: PersistedComparisonType;
}): boolean => {
  const baselines = isPaceMatrix(chartDef)
    ? PACE_BASELINES
    : DATE_COMPARISON_BASELINES;
  return (
    comparison.compareType === 'compareTo' &&
    _.isEqual(_.sortBy(Object.keys(comparison)), [
      'compareType',
      'relativeDateOption',
    ]) &&
    !!comparison.relativeDateOption &&
    !!baselines[comparison.relativeDateOption]
  );
};

const toServerSortBy = (
  sort: Studio.TableSort | undefined,
): VisualisationDefinitions.ServerSortBy =>
  sort && sort.by === 'dimension'
    ? `alpha (${sort.direction})`
    : `metric (${sort ? sort.direction : 'desc'})`;

const toClientSortBy = (
  sort: Studio.TableChangeSort | undefined,
): VisualisationDefinitions.ClientSortBy | undefined =>
  sort
    ? (`${sort.by}${sort.isPercent ? ' %' : ''} (${sort.direction})` as VisualisationDefinitions.ClientSortBy)
    : undefined;

// The legacy chart definition a date comparison or pace Table saves as and
// draws with (components/SingleMetricDateMatrix, components/PaceMatrix):
// its first metric, by its first dimension, which must be a text field.
const buildLegacyMatrixDefinition = ({
  card,
  id,
  name,
}: {
  card: LegacyMatrixContent;
  id: string;
  name: string;
}): { chartDefinition: LegacyMatrix } | { missing: string } => {
  const [metricId] = card.metricIds ?? [];
  const [field] = card.dimensions ?? [];
  if (!metricId) {
    return { missing: 'Add a metric' };
  }
  if (!field) {
    return { missing: 'Add a dimension for the rows' };
  }
  if ((card.dimensionOptions ?? {})[field]) {
    return { missing: 'Group the rows by a text field' };
  }
  const table = card.table ?? {};
  const metricOptions = (table.metrics ?? {})[metricId] ?? {};
  const common = {
    id,
    name,
    description: card.description ?? '',
    metricId,
    ...(metricOptions.displayName
      ? { metricDisplayName: metricOptions.displayName }
      : {}),
    groupByField: field,
  };

  if (table.rows === 'pace') {
    const pace = table.pace ?? {};
    return {
      chartDefinition: {
        ...common,
        type: 'PaceMatrix',
        hasTotalsCol: !pace.isTotalHidden,
        hasPaceCol: !pace.isPaceHidden,
        isTotalsAndPacePinnedRight: !pace.isUnpinned,
        interval: 'day',
        isCardMode: !!pace.isCard,
        sortOrder: pace.sort ?? DEFAULT_PACE_SORT,
        isShowingOnlyPaceCol: !!pace.isPaceOnly,
      },
    };
  }

  const dateComparison = table.dateComparison ?? {};
  const clientSortBy = toClientSortBy(dateComparison.changeSort);
  return {
    chartDefinition: {
      ...common,
      type: 'SingleMetricDateMatrix',
      interval: dateComparison.interval ?? DEFAULT_DATE_COMPARISON_INTERVAL,
      limit:
        table.limit && table.limit.n
          ? table.limit.n
          : DEFAULT_DATE_COMPARISON_LIMIT,
      serverSortBy: toServerSortBy(table.sort),
      ...(clientSortBy ? { clientSortBy } : {}),
      comparisonCellFormat: metricOptions.cellType ?? 'delta',
      ...(table.gridSizing === 'fitContent'
        ? { isSizedToFitOverridden: true }
        : {}),
    },
  };
};

const fromClientSortBy = (
  clientSortBy: VisualisationDefinitions.ClientSortBy | undefined,
): Studio.TableChangeSort | undefined => {
  const match = clientSortBy
    ? /^(improvement|change)( %)? \((asc|desc)\)$/.exec(clientSortBy)
    : null;
  if (!match) {
    return undefined;
  }
  const sort: Studio.TableChangeSort = {
    by: match[1] as Studio.TableChangeSort['by'],
    direction: match[3] as Studio.TableChangeSort['direction'],
  };
  return match[2] ? { ...sort, isPercent: true } : sort;
};

const fromServerSortBy = (
  serverSortBy: VisualisationDefinitions.ServerSortBy,
): Studio.TableSort | undefined => {
  const match = /^(alpha|metric) \((asc|desc)\)$/.exec(serverSortBy);
  if (!match || serverSortBy === 'metric (desc)') {
    return undefined;
  }
  return {
    by: match[1] === 'alpha' ? 'dimension' : 'metric',
    direction: match[2] as Studio.TableSort['direction'],
  };
};

// The baseline a gadget's comparison gives (absent for the default). A
// report gadget without one follows the report's; a dashboard one without
// one opens with the default (as the classic editor sets one when it's
// opened).
const guessBaseline = ({
  comparison,
  isReportGadget,
  defaultBaseline,
}: {
  comparison: PersistedComparisonType | undefined;
  isReportGadget: boolean;
  defaultBaseline: RelativeDateRangeType;
}): Studio.TableBaseline | undefined => {
  if (!comparison) {
    return isReportGadget ? 'report' : undefined;
  }
  return comparison.relativeDateOption === defaultBaseline
    ? undefined
    : comparison.relativeDateOption;
};

// Without undefined values; undefined when nothing is left
const compact = <T extends object>(value: T): T | undefined => {
  const compacted = _.omitBy(value, _.isUndefined);
  return _.isEmpty(compacted) ? undefined : (compacted as T);
};

const guessPaceOptions = ({
  chartDef,
  baseline,
}: {
  chartDef: VisualisationDefinitions.PaceMatrix;
  baseline: Studio.TableBaseline | undefined;
}): Studio.TableOptions => ({
  rows: 'pace',
  ...compact({
    pace: compact({
      baseline,
      isTotalHidden: !chartDef.hasTotalsCol || undefined,
      isPaceHidden: !chartDef.hasPaceCol || undefined,
      isPaceOnly: chartDef.isShowingOnlyPaceCol || undefined,
      isUnpinned: !chartDef.isTotalsAndPacePinnedRight || undefined,
      isCard: chartDef.isCardMode || undefined,
      sort: _.isEqual(chartDef.sortOrder, DEFAULT_PACE_SORT)
        ? undefined
        : chartDef.sortOrder,
    }),
    metrics: chartDef.metricDisplayName
      ? { [chartDef.metricId]: { displayName: chartDef.metricDisplayName } }
      : undefined,
  }),
});

const guessDateComparisonOptions = ({
  chartDef,
  baseline,
}: {
  chartDef: VisualisationDefinitions.SingleMetricDateMatrix;
  baseline: Studio.TableBaseline | undefined;
}): Studio.TableOptions => {
  const metricOptions = compact<Studio.TableMetricOptions>({
    displayName: chartDef.metricDisplayName || undefined,
    cellType:
      chartDef.comparisonCellFormat === 'delta' ||
      chartDef.comparisonCellFormat === 'none'
        ? undefined
        : chartDef.comparisonCellFormat,
  });
  return {
    rows: 'dateComparison',
    ...compact({
      dateComparison: compact<Studio.TableDateComparison>({
        baseline,
        interval:
          chartDef.interval === DEFAULT_DATE_COMPARISON_INTERVAL
            ? undefined
            : chartDef.interval,
        changeSort: fromClientSortBy(chartDef.clientSortBy),
      }),
      sort: fromServerSortBy(chartDef.serverSortBy),
      limit:
        chartDef.limit === DEFAULT_DATE_COMPARISON_LIMIT
          ? undefined
          : { mode: 'top' as const, n: chartDef.limit },
      gridSizing: chartDef.isSizedToFitOverridden
        ? ('fitContent' as const)
        : undefined,
      metrics: metricOptions
        ? { [chartDef.metricId]: metricOptions }
        : undefined,
    }),
  };
};

// A Table block's settings from a saved Single Metric Date Matrix or Pace
// Matrix, and its gadget's comparison. Whether they rebuild it is checked
// separately (reproducesLegacyMatrix).
export const guessLegacyMatrixCard = ({
  chartDef,
  comparison,
  isReportGadget,
}: {
  chartDef: LegacyMatrix;
  comparison: PersistedComparisonType | undefined;
  isReportGadget: boolean;
}): Studio.GadgetSidecarCard => {
  const baseline = guessBaseline({
    comparison,
    isReportGadget,
    defaultBaseline: isPaceMatrix(chartDef)
      ? DEFAULT_PACE_BASELINE
      : DEFAULT_DATE_COMPARISON_BASELINE,
  });
  return {
    visType: 'table',
    metricIds: [chartDef.metricId],
    name: chartDef.name,
    ...(chartDef.description ? { description: chartDef.description } : {}),
    dimensions: [chartDef.groupByField],
    table: isPaceMatrix(chartDef)
      ? guessPaceOptions({ chartDef, baseline })
      : guessDateComparisonOptions({ chartDef, baseline }),
  };
};

// Without what the classic editor saves as empty, what's kept for soft
// deletes, and the version an old migration left on some, which nothing
// reads (migrationsDeprecated/SingleMetricDateMatrix). Pace matrices saved
// before "Only Show Total/Pace" don't have it.
const toComparable = (def: LegacyMatrix) =>
  _.omitBy(
    {
      ..._.omit(def, ['isHidden', 'hiddenOn', 'version']),
      description: def.description || undefined,
      metricDisplayName: def.metricDisplayName || undefined,
      ...(isSingleMetricDateMatrix(def)
        ? {
            clientSortBy: def.clientSortBy || undefined,
            isSizedToFitOverridden: def.isSizedToFitOverridden || undefined,
          }
        : { isShowingOnlyPaceCol: def.isShowingOnlyPaceCol || undefined }),
    },
    _.isUndefined,
  );

// What the settings don't rebuild of the saved matrix and its gadget's
// comparison, by setting (empty when they rebuild it all). Its name is the
// gadget's own (the classic editor can change it). A dashboard gadget saved
// without a comparison matches the default baseline.
export const getLegacyMatrixChanges = ({
  card,
  chartDef,
  comparison,
  isReportGadget,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: LegacyMatrix;
  comparison: PersistedComparisonType | undefined;
  isReportGadget: boolean;
}): string[] => {
  if (
    !isLegacyMatrixTable(card) ||
    (card.table?.rows === 'pace') !== isPaceMatrix(chartDef)
  ) {
    return ['Table rows'];
  }
  const built = buildLegacyMatrixDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
  });
  if (!('chartDefinition' in built)) {
    return [built.missing];
  }
  const before = toComparable(chartDef) as Record<string, unknown>;
  const after = toComparable(built.chartDefinition) as Record<string, unknown>;
  const saved = getLegacyMatrixComparison(card.table);
  const isSameComparison =
    _.isEqual(saved, comparison) ||
    (!comparison && !isReportGadget && !getBaseline(card.table));
  return [
    ..._.uniq([...Object.keys(before), ...Object.keys(after)])
      .filter((key) => !_.isEqual(before[key], after[key]))
      .map((key) => _.startCase(key)),
    ...(isSameComparison ? [] : ['Comparison']),
  ];
};

// Whether the settings rebuild the saved matrix and its gadget's comparison
export const reproducesLegacyMatrix = (
  args: Parameters<typeof getLegacyMatrixChanges>[0],
): boolean => getLegacyMatrixChanges(args).length === 0;

export default buildLegacyMatrixDefinition;
